import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AsyncSelect from 'react-select/async'
import styled from 'styled-components'
import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import Handle from '~/prix/react/components/handle'
import Modal from '~/prix/react/components/modal'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import useAPI from '~/prix/react/hooks/api'
import useItems from '~/prix/react/hooks/items'
import geoListDefinition from '../geo/list/geoList.definition'
import {
  childrenItemViewLabels,
  compatibleSpaceChildren,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '../variable/spaceTime.data'
import {
  Layer,
  Space,
  SpaceChildrenGeoStandard,
  SpaceChildrenItem,
  SpaceGeoStandardType,
  SpaceType,
} from '../variable/spaceTime.types'
import debounce from 'debounce-promise'
import DemarcationIcon from '~/components/icons/design/64px_draw-polygon.svg'
import ModalSpaceDemarcation from './modalSpaceDemarcation.component'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import { colors } from '~/design'
import AgentAreaDemarcationQuery from '../agentReport/queries/agentAreaDemarcationQuery.query'
import type { MultiPolygon, Polygon } from 'geojson'
import {
  AGENT_POLYGON_CPF_PROPERTY,
  extractStringEqualityValue,
} from './extractStringEqualityValue.util'
import {
  agentPolygonDemarcationSource,
  buildDemarcationFeatureWithHash,
  buildLayerWithCustomPolygonDelimitation,
  type DemarcationFeatureWithHash,
} from './customPolygonDemarcation.util'
import { VariableAndOptionalResult } from '../variable/variables.typing'
import { VariableCollectionsAndOptionalResult } from '../variableCollection/variableCollection.typing'
import agentPolygonSmartMappingDefinition from './agentPolygonSmartMapping/agentPolygonSmartMapping.definition'
import type { SmartMappingAgentAreaDemarcationResultItem } from './agentPolygonSmartMapping/agentPolygonSmartMappingLayer.util'
import splitFilterByProperty from './splitFilterByProperty.util'

type DelimitationOption = {
  id: number
  name: string
  type: SpaceGeoStandardType
  searchRanking?: number
}

type LegacyAgentAreaDemarcationResultItem = {
  serviceArea: Polygon | MultiPolygon | null
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-height: 70vh;

  .top {
    display: flex;
    flex-direction: row;
    padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(1)};
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px 8px 0 0;

    .input-wrapper {
      position: relative;
      border-radius: 8px 8px 0 0;
      width: 100%;
      flex: 1;
      padding-right: 5px;
    }

    .button-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      border-top-right-radius: 8px;
      position: relative;

      button {
        height: 40px;
        width: 40px;
        background-color: #fff;
        border-style: none;
        cursor: pointer;
        justify-content: center;
        align-items: center;
        border: 1px solid #cccccc;
        border-radius: 8px;
        box-shadow: none;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: border-color 0.3s ease-in-out;

        &:hover {
          border: 1px solid #adadad;
        }
      }
    }
  }

  .children-options {
    flex: 1;

    overflow-x: hidden;
    overflow-y: scroll;

    .children-option {
      display: flex;
      position: relative;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      cursor: pointer;

      &:not(:last-child)::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        margin-left: 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
        width: 100%;
      }

      &:hover {
        background-color: ${({ theme }) => theme.colors.lightGrey};

        &:last-child {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }
      }

      .title {
        display: flex;
        align-items: center;
        font-weight: bold;
        gap: 4px;
      }

      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        flex-direction: column;
        align-items: flex-start;

        .right {
          margin-top: 1rem;
          justify-content: flex-start;
        }
      }
    }

    .non-clickable {
      display: flex;
      place-content: center;
      padding: 2rem;

      .title {
        display: flex;
        align-items: center;
        font-weight: bold;
        gap: 4px;
      }
    }
  }
`

interface ViewSelectModalSpaceProps {
  layer: Layer
  isOpen: boolean
  onClose: () => void
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
}

export default function ViewSelectModalSpace({
  layer,
  isOpen,
  onClose,
  onChangeLayer,
}: ViewSelectModalSpaceProps) {
  const { context } = useAPI()

  const [currentLayer, setCurrentLayer] = useState<Layer>(layer)
  const [polygonDemarcation, setPolygonDemarcation] = useState<
    DemarcationFeatureWithHash | null | undefined
  >(undefined)
  const [isOpenDemarcationModal, setIsOpenDemarcationModal] = useState<boolean>(false)

  useEffect(() => {
    setCurrentLayer(layer)
    setPolygonDemarcation(undefined)
  }, [layer])

  const selectedAgentPolygonActionDefinitionKey =
    (currentLayer.variable as VariableAndOptionalResult)?.actionDefinitionKey ?? null
  const shouldUseSmartMappingForLegalEntityStatsPreset =
    selectedAgentPolygonActionDefinitionKey === 'legalEntityStats' &&
    currentLayer.variable.presetKey === 'attendancePercentageAppSebraeInCurrentYear'
  const shouldUseLegacyAgentPolygonDemarcation =
    selectedAgentPolygonActionDefinitionKey === 'legalEntityStats' &&
    !shouldUseSmartMappingForLegalEntityStatsPreset
  const shouldUseSmartMappingAgentPolygonDemarcation =
    selectedAgentPolygonActionDefinitionKey === 'appSebraeAttendanceStats' ||
    selectedAgentPolygonActionDefinitionKey === 'legalEntityAppSebraeAttendanceStats' ||
    shouldUseSmartMappingForLegalEntityStatsPreset

  const selectedAgentPolygonCpf = useMemo(() => {
    if (
      !selectedAgentPolygonActionDefinitionKey ||
      ![
        'legalEntityStats',
        'appSebraeAttendanceStats',
        'legalEntityAppSebraeAttendanceStats',
      ].includes(selectedAgentPolygonActionDefinitionKey)
    ) {
      return null
    }

    return extractStringEqualityValue(
      (currentLayer.variable as VariableAndOptionalResult).filter,
      'agentPolygonCpf',
    )
  }, [
    (currentLayer.variable as VariableAndOptionalResult).filter,
    selectedAgentPolygonActionDefinitionKey,
  ])

  const agentPolygonTimeRange = useMemo(
    () => ({
      startDate: 'startDate' in currentLayer.time ? currentLayer.time.startDate : undefined,
      endDate: 'endDate' in currentLayer.time ? currentLayer.time.endDate : undefined,
    }),
    [currentLayer.time],
  )

  const agentPolygonScopedFilter = useMemo(() => {
    const filter = (currentLayer.variable as VariableAndOptionalResult)?.filter
    if (!!filter) {
      return splitFilterByProperty(filter, 'agentPolygonCpf').remainingFilter
    }

    return null
  }, [(currentLayer.variable as VariableAndOptionalResult).filter])

  const { callAction } = useActionFunction(geoListDefinition)

  const agentAreaDemarcation = useItems(
    () =>
      selectedAgentPolygonCpf
        ? AgentAreaDemarcationQuery(selectedAgentPolygonCpf)
        : (null as never),
    [selectedAgentPolygonCpf],
    {
      cache: 60 * 60,
      autoLoad: Boolean(
        isOpen && selectedAgentPolygonCpf && shouldUseLegacyAgentPolygonDemarcation,
      ),
    },
  )

  const agentPolygonDemarcation = useMemo(() => {
    const serviceArea =
      (agentAreaDemarcation.items?.[0]?.serviceArea as Polygon | MultiPolygon | undefined) ?? null

    return buildDemarcationFeatureWithHash(serviceArea, agentPolygonDemarcationSource)
  }, [agentAreaDemarcation.items])

  const currentCustomPolygonDemarcation = useMemo(() => {
    if (currentLayer.space.delimitationType !== 'custom') {
      return null
    }

    return currentLayer.space.delimitationCustom as DemarcationFeatureWithHash
  }, [currentLayer.space])

  const resolvedPolygonDemarcation = useMemo(() => {
    if (polygonDemarcation !== undefined) {
      return polygonDemarcation
    }

    return currentCustomPolygonDemarcation ?? agentPolygonDemarcation ?? null
  }, [agentPolygonDemarcation, currentCustomPolygonDemarcation, polygonDemarcation])

  const isEadEnrollmentCategory = (categoryKey: string | number | undefined) =>
    categoryKey === 'eadEnrollment' ||
    categoryKey === 'eadEnrollmentCourses' ||
    categoryKey === 'eadEnrollmentCoursesOutsidePortfolio' ||
    categoryKey === 'monitor' ||
    categoryKey === 'monitorProducts'

  const allowedDelimitationTypesForEadEnrollment = useMemo(
    () => new Set<SpaceGeoStandardType>(['state', 'macroRegion', 'country']),
    [],
  )

  const coerceChildrenType = (targetLayer: Layer, requestedType: SpaceType): SpaceType => {
    if (!isEadEnrollmentCategory(targetLayer.variable.categoryKey)) {
      return requestedType
    }

    if (
      requestedType === 'state' ||
      requestedType === 'macroRegion' ||
      requestedType === 'country'
    ) {
      return requestedType
    }

    return 'state'
  }

  const shouldDisplayChildrenOption = (targetLayer: Layer, optionType: SpaceType) => {
    if (!isEadEnrollmentCategory(targetLayer.variable.categoryKey)) {
      return true
    }

    return (
      optionType === 'city' ||
      optionType === 'state' ||
      optionType === 'macroRegion' ||
      optionType === 'country'
    )
  }

  const handleSelectChildrenOption = (key: Exclude<SpaceType, 'custom'>) => {
    if (!context.user) {
      return
    }

    setCurrentLayer(oldLayer => {
      const coercedType = coerceChildrenType(oldLayer, key)
      const isSameAsDelimitation = coercedType === oldLayer.space.delimitationType

      const space = {
        ...oldLayer.space,
        childrenType: coercedType,
        childrenItemView: undefined,
        childrenH3Resolution: undefined,
        childrenTitle: isSameAsDelimitation
          ? spaceTypeSingularLabels[coercedType]
          : spaceTypePluralLabels[coercedType],
      } as Layer['space']

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const handleSelectChildrenViewOption = (key: 'item', childrenItemViewKey: string) => {
    if (!context.user) {
      return
    }

    setCurrentLayer(oldLayer => {
      const coercedType = coerceChildrenType(oldLayer, key)

      if (coercedType !== 'item') {
        const space = {
          ...oldLayer.space,
          childrenType: coercedType,
          childrenItemView: undefined,
          childrenH3Resolution: undefined,
          childrenTitle:
            coercedType === oldLayer.space.delimitationType
              ? spaceTypeSingularLabels[coercedType]
              : spaceTypePluralLabels[coercedType],
        } as Space & { childrenItemView?: string; childrenH3Resolution?: number }

        return {
          ...oldLayer,
          space,
        }
      }

      const space = {
        ...oldLayer.space,
        childrenType: key,
        childrenItemView: childrenItemViewKey,
        childrenH3Resolution: undefined,
      } as Space

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const handleSetDelimitationFromSelect = (
    item: {
      id: number
      name: string
      subtitle?: string
      type: string
    } | null,
  ) => {
    if (!item || !context.user) {
      return
    }

    setPolygonDemarcation(undefined)
    setCurrentLayer(oldLayer => {
      const newCompatibleChildren = (layer.variable as VariableAndOptionalResult)
        .compatibleChildrenTypes
        ? compatibleSpaceChildren[item.type as SpaceType].filter(type =>
            (layer.variable as VariableAndOptionalResult).compatibleChildrenTypes!.includes(type),
          )
        : compatibleSpaceChildren[item.type as SpaceType]

      let childrenType: SpaceType = oldLayer.space.childrenType
      let childrenTitle = (oldLayer.space as SpaceChildrenGeoStandard).childrenTitle
      let childrenItemView = (oldLayer.space as SpaceChildrenItem).childrenItemView
      if (!newCompatibleChildren.includes(childrenType)) {
        childrenType = newCompatibleChildren[0] as SpaceType

        if (childrenType === 'item') {
          childrenItemView = 'heatmap'
          childrenTitle = childrenItemViewLabels[childrenItemView]
        } else {
          childrenTitle = spaceTypePluralLabels[childrenType]
        }
      }

      const space = {
        ...oldLayer.space,
        childrenTitle,
        childrenItemView,
        childrenType,
        childrenH3Resolution: undefined,
        delimitationId: item.id,
        delimitationTitle: item.name,
        delimitationType: item.type,
      } as Space

      return {
        ...oldLayer,
        space,
      }
    })
  }

  const handleSetDelimitationFromCustomPolygon = (
    newPolygonDemarcation: DemarcationFeatureWithHash | null,
  ) => {
    setPolygonDemarcation(newPolygonDemarcation)

    if (!newPolygonDemarcation) {
      return
    }

    setCurrentLayer(oldLayer =>
      buildLayerWithCustomPolygonDelimitation(oldLayer, newPolygonDemarcation),
    )
  }

  const isVariableCollection = !!(currentLayer.variable as VariableCollectionsAndOptionalResult)
    .isVariableCollection

  const collectionSpacesToAnalyze = useMemo(() => {
    if (!isVariableCollection) {
      return undefined
    }

    const spaces = (currentLayer.variable as VariableCollectionsAndOptionalResult).variables.reduce<
      Space[]
    >((mergedSpaces, variable) => {
      variable.spacesToAnalyze?.forEach(space => {
        if (space.delimitationType === 'custom') {
          return
        }

        const childrenType =
          space.childrenType === 'item' ? space.delimitationType : space.childrenType

        if (childrenType === 'custom') {
          return
        }

        const isAlreadyIncluded = mergedSpaces.some(
          mergedSpace =>
            mergedSpace.delimitationType === space.delimitationType &&
            mergedSpace.childrenType === childrenType &&
            'delimitationId' in mergedSpace &&
            mergedSpace.delimitationId === space.delimitationId,
        )

        if (!isAlreadyIncluded) {
          mergedSpaces.push({
            ...space,
            childrenType,
            childrenTitle:
              space.childrenType === 'item'
                ? spaceTypeSingularLabels[space.delimitationType]
                : space.childrenTitle,
          })
        }
      })

      return mergedSpaces
    }, [])

    return spaces.length ? spaces : undefined
  }, [currentLayer.variable, isVariableCollection])

  const delimitationOptions = useCallback(
    async (
      inputValue: string,
    ): Promise<
      Array<{
        id: number
        name: string
        type: SpaceGeoStandardType
      }>
    > => {
      const { resultPromise } = callAction({
        query: inputValue.trim(),
        limit: 1500,
        include: [],
        isSearch: true,
        spaces: collectionSpacesToAnalyze,
      })
      const result = await resultPromise
      let items = result.items

      if (items.length > 0 && items[0].searchRanking) {
        items = items
          .filter((item: { searchRanking: number }) => item.searchRanking > 0)
          .sort(
            (a: { searchRanking: number }, b: { searchRanking: number }) =>
              Number(b.searchRanking) - Number(a.searchRanking),
          )
      }

      if (isEadEnrollmentCategory(currentLayer.variable.categoryKey)) {
        items = items.filter((item: any) =>
          allowedDelimitationTypesForEadEnrollment.has(item.type as SpaceGeoStandardType),
        )
      }

      const seenItems = new Set<string>()
      items = items.filter((item: any) => {
        const key = `${item.type}-${item.id}`
        if (seenItems.has(key)) {
          return false
        }

        seenItems.add(key)
        return true
      })

      return items
    },
    [
      callAction,
      collectionSpacesToAnalyze,
      currentLayer.variable.categoryKey,
      allowedDelimitationTypesForEadEnrollment,
    ],
  )

  const debouncedLoadOptions = useMemo(
    () => debounce(delimitationOptions, 500),
    [delimitationOptions, 500],
  )

  const baseCompatibleChildren = (layer.variable as VariableAndOptionalResult)
    .compatibleChildrenTypes
    ? compatibleSpaceChildren[currentLayer.space.delimitationType].filter(type =>
        (layer.variable as VariableAndOptionalResult).compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[currentLayer.space.delimitationType]

  const currentCompatibleChildren = baseCompatibleChildren.filter(type =>
    shouldDisplayChildrenOption(currentLayer, type),
  )

  const dedupedCompatibleChildren = useMemo(() => {
    const seen = new Map<SpaceType, SpaceType>()
    const ordered: SpaceType[] = []

    currentCompatibleChildren.forEach(optionType => {
      const coercedType = coerceChildrenType(currentLayer, optionType)
      const existingOption = seen.get(coercedType)

      if (!existingOption) {
        seen.set(coercedType, optionType)
        ordered.push(optionType)
        return
      }

      if (optionType === coercedType && existingOption !== coercedType) {
        const existingIndex = ordered.indexOf(existingOption)
        if (existingIndex !== -1) {
          ordered[existingIndex] = optionType
        }
        seen.set(coercedType, optionType)
      }
    })

    return ordered
  }, [currentCompatibleChildren, currentLayer])

  const handleCancel = () => {
    setCurrentLayer(layer)
    setPolygonDemarcation(undefined)
    onClose()
  }

  const handleConfirm = () => {
    onChangeLayer(currentLayer)
    onClose()
  }

  const onResetDemarcation = () => {
    setPolygonDemarcation(undefined)
  }
  const formatOptionLabel = (item: {
    id: number
    name: string
    type: string
    subtitle?: string
  }) => (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '0.2rem 0' }}>
      <span>{item?.name}</span>
      <span style={{ color: '#a5a4a4', fontSize: '0.8rem' }}>
        {spaceTypeSingularLabels[item?.type as SpaceType]}{' '}
        {item?.subtitle ? ' > ' + item?.subtitle : ''}
      </span>
    </div>
  )

  const selectValue =
    currentLayer.space.delimitationType !== 'custom' && currentLayer.space.delimitationId
      ? {
          id: currentLayer.space.delimitationId,
          name: currentLayer.space.delimitationTitle || 'Carregando...',
          type: currentLayer.space.delimitationType,
        }
      : {
          id: -1,
          name: 'Demarcação customizada',
          type: 'custom' as SpaceType,
        }

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalStyle='sentinel'>
      <Handle>
        <Wrapper>
          <div className='top'>
            <div className='input-wrapper'>
              <AsyncSelect
                cacheOptions
                defaultOptions
                value={selectValue}
                loadOptions={debouncedLoadOptions}
                getOptionValue={item => `${item.id}-${item.type}`}
                formatOptionLabel={formatOptionLabel}
                noOptionsMessage={() => 'Sem resultados'}
                loadingMessage={() => 'Carregando...'}
                placeholder='Delimitação'
                onChange={handleSetDelimitationFromSelect}
                styles={{
                  control: baseStyles => ({
                    ...baseStyles,
                    height: '40px',
                    borderRadius: '8px',
                  }),
                }}
              />
            </div>

            {!isVariableCollection && (
              <div className='button-wrapper'>
                <button
                  onClick={() => setIsOpenDemarcationModal(true)}
                  title='Demarcação customizada'
                >
                  <div style={{ marginTop: '4px' }}>
                    <DemarcationIcon fill={'#0C2340'} width={22} height={22} />
                  </div>
                </button>
              </div>
            )}
          </div>

          {true && (
            <Modal
              isOpen={isOpenDemarcationModal}
              onClose={() => setIsOpenDemarcationModal(false)}
              modalStyle='sentinel'
            >
              <ModalSpaceDemarcation
                onCloseModalDemarcation={() => setIsOpenDemarcationModal(false)}
                setPolygonDemarcation={handleSetDelimitationFromCustomPolygon}
                polygonDemarcation={resolvedPolygonDemarcation}
              />
            </Modal>
          )}

          {!isVariableCollection ? (
            <div className='children-options'>
              {dedupedCompatibleChildren.length ? (
                dedupedCompatibleChildren.map((key, index) => {
                  const coercedType = coerceChildrenType(currentLayer, key as SpaceType)
                  const name =
                    coercedType !== currentLayer.space.delimitationType
                      ? spaceTypePluralLabels[coercedType]
                      : spaceTypeSingularLabels[coercedType]
                  return (
                    <Fragment key={index}>
                      {(key as SpaceType) === 'item' ? (
                        Object.keys(childrenItemViewLabels)
                          .filter(itemType => itemType !== 'arc')
                          .map((childrenItemViewLabelsKey, index) => {
                            return (
                              <React.Fragment key={index}>
                                <article
                                  className='children-option'
                                  role='button'
                                  onClick={() => {
                                    handleSelectChildrenViewOption(
                                      'item',
                                      childrenItemViewLabelsKey,
                                    )
                                  }}
                                >
                                  <div className='title'>
                                    {(currentLayer.space as SpaceChildrenItem).childrenItemView ===
                                      childrenItemViewLabelsKey && (
                                      <FontAwesomeIcon icon={faCheckCircle} />
                                    )}
                                    {
                                      childrenItemViewLabels[
                                        childrenItemViewLabelsKey as keyof typeof childrenItemViewLabels
                                      ]
                                    }
                                  </div>
                                </article>
                              </React.Fragment>
                            )
                          })
                      ) : (
                        <article
                          key={index}
                          className='children-option'
                          role='button'
                          onClick={() => {
                            handleSelectChildrenOption(key as Exclude<SpaceType, 'custom'>)
                            onResetDemarcation()
                          }}
                        >
                          <div className='title'>
                            {currentLayer.space.childrenType === coercedType && (
                              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
                            )}
                            {name}
                          </div>
                        </article>
                      )}
                    </Fragment>
                  )
                })
              ) : (
                <article className='non-clickable'>
                  <div className='title'>
                    <AlertIcon fill={colors.darkGray} width={18} height={18} />
                    <span>
                      A visualização, neste nível geográfico, não é compatível com a seleção atual
                    </span>
                  </div>
                </article>
              )}
            </div>
          ) : (
            <div className='children-options'>
              <article className='non-clickable'>
                <div className='title'>
                  <AlertIcon fill={colors.darkGray} width={18} height={18} />
                  <span>Existe uma pré-definição de visualização para esta variável.</span>
                </div>
              </article>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              padding: '10px',
              height: '50px',
              justifyContent: 'right',
            }}
          >
            <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleCancel}>
              Cancelar
            </ButtonLinkAsSimpleText>
            <ButtonLink
              onGo={handleConfirm}
              isDisabled={dedupedCompatibleChildren.length > 0 ? false : true}
            >
              Confirmar
            </ButtonLink>
          </div>
        </Wrapper>
      </Handle>
    </Modal>
  )
}
